import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';

import Currencies from '@/utils/Currencies';
import SearchStore from '@/stores/SearchStore';
import DealConstants from '@/constants/DealConstants';
import DealUtils from '@/entities/DealUtils';
import Constants from '@/components/pages/results/deal/constants';

import { isExpediaDealOf } from './Rm3DealPrice';

const { BOOKING_PROVIDER_STRING } = Constants;
const BOOKING_LOGO_URL = 'https://arbitrip.s3.amazonaws.com/booking_logo.png';

function isInBasketOf(basket, deal) {
    return !!(basket && Array.isArray(basket.deals) && basket.deals.some((basketDeal) => basketDeal.id === deal.id));
}

function bookAriaLabelOf(deal, profile, roomName, t) {
    const totalPrice = Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
        PriceBreakdown.isBreakdownPrice({ profile, deal }) ? deal.expedia_pricing?.totalPrice : deal.totalPrice,
        profile.display_currency_exchange_rate,
        profile.display_currency,
        0,
    );
    const pricePerNight = Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
        deal.pricePerNight,
        _.get(profile, 'display_currency_exchange_rate'),
        _.get(profile, 'display_currency'),
        0,
    );
    const showTotalPrice = PriceBreakdown.isShowTotalPrice({ profile }) || profile.agent;
    return showTotalPrice
        ? t('BookRoomForTotal', { roomName, price: totalPrice })
        : t('BookRoomForPerNight', { roomName, price: pricePerNight });
}

/**
 * Book / Add-to-list / "Checking availability" / disabled Book, and the hover "Add to list" link — ported from
 * `DealComponent.render` (`DEAL_ACTION`, `ADD_TO_BASKET_ACTION`, the status switch, `BOOKING_LOGO`).
 */
function Rm3BookAction({
    deal,
    profile,
    basket,
    roomName,
    hovered,
    addToListDisabled,
    onBook,
    onAddToList,
    onRemoveFromList,
}) {
    const { t } = useTranslation();

    const hideBasket = _.get(profile, 'company.partners_ui.hide_basket', false);
    const faultDeal = deal && deal.faultDeal;
    const dealStatus = deal && deal.status;
    const isSuccessful = dealStatus === DealConstants.DEAL_STATUS.SUCCESS;
    const notAvailable =
        dealStatus === DealConstants.DEAL_STATUS.NA || dealStatus === DealConstants.DEAL_STATUS.FAIL || faultDeal;
    const blockReserving = profile.blocked_reserving && !SearchStore.isPrivateTravel();

    if (dealStatus === DealConstants.DEAL_STATUS.INITIAL || dealStatus === DealConstants.DEAL_STATUS.BUSY) {
        return (
            <div className="checking-availability">
                <i title={t('CheckingAvailability')} className="fa fa-spin fa-spinner" />
                <span>{t('CheckingAvailability')}</span>
            </div>
        );
    }

    const inBasket = isInBasketOf(basket, deal);
    const addOrRemove = inBasket ? () => onRemoveFromList(deal) : () => onAddToList(deal);
    const blockedReservingClickHandler = addToListDisabled ? _.noop : addOrRemove;
    const blockedReservingText = inBasket ? t('RemoveFromList') : t('AddToList');
    const buttonClasses = blockReserving ? ' block-reserving' + (inBasket ? ' active' : '') : '';
    const bookButtonText = blockReserving ? blockedReservingText : notAvailable ? t('NotAvailable') : t('Book');
    const isExpediaDeal = isExpediaDealOf(deal);

    const dealAction =
        (isSuccessful || deal.is_lazy_recheck) && !faultDeal ? (
            <div key="book-deal" className="deal-action book-action">
                <Button
                    aria-label={bookAriaLabelOf(deal, profile, roomName, t)}
                    className={'book-btn' + buttonClasses}
                    variant="contained"
                    color="primary"
                    onClick={blockReserving ? blockedReservingClickHandler : () => onBook(deal)}
                    disabled={blockReserving}
                    disableRipple
                    data-testid="book-deal-button"
                    data-booking-safe-for-test={isExpediaDeal}
                >
                    {bookButtonText}
                </Button>
            </div>
        ) : (
            <div key="disabled-book-deal" className="deal-action book-action">
                <Button
                    className="book-btn default-style"
                    variant="contained"
                    color="primary"
                    disabled
                    aria-disabled="true"
                >
                    {bookButtonText}
                </Button>
            </div>
        );

    const addToBasketAction =
        hovered && !blockReserving && !faultDeal && !hideBasket ? (
            <a
                href="#!"
                className="deal-action share-action"
                onClick={addToListDisabled ? _.noop : addOrRemove}
                key={inBasket ? 'remove-deal' : 'share-deal'}
                disabled={addToListDisabled}
                data-testid="rm3-add-to-list"
            >
                {inBasket ? t('RemoveFromList') : t('AddToList')}
            </a>
        ) : null;

    const actionItems = isSuccessful ? [addToBasketAction, dealAction] : [dealAction];
    const showBookingLogo =
        profile.show_supplier_logo_next_to_rate_enabled &&
        DealUtils.isValidDeal(deal) &&
        deal.id.startsWith(BOOKING_PROVIDER_STRING);

    return (
        <>
            <div className="deal-actions flex align-center">{actionItems}</div>
            {showBookingLogo ? <img className="booking-logo" src={BOOKING_LOGO_URL} /> : null}
        </>
    );
}

export default Rm3BookAction;
